<template>
  <div class="row teams-wrap">
    <div class="col-6">
      <Panel class="h-100">
        <div class="d-flex justify-content-between">
          <Subheader
            class="mb-6"
            :text="$t('_web_create-clan-team_members-to-add', 'Members to Add')"
          />
          <HeraSearchInput
            v-model="userFilter"
            :placeholder="
              $t('_web_create-clan-team_search-placeholder', 'Search...')
            "
            class="search-field p-0 m-0 w-50"
            @change="onFilter"
          />
        </div>

        <ul
          v-if="hasUsers"
          class="list-group list-group-flush bg-transparent w-100"
        >
          <ListItemAccount
            v-for="user in filteredUsers"
            :key="user.login"
            tag="li"
            class="py-3 px-3 font-weight-bold"
            :size="24"
            v-bind="{
              type: user.type,
              avatar: user.avatar,
              name: $filters.displayName(user),
              accountId: user.login,
              vanityId: user.vanityId || user.vanity_id,
            }"
          >
            <div slot="action" class="col-auto">
              <Icon
                name="arrow-triangle"
                width="24"
                class="cursor-pointer link-add-user"
                @click.native="changeUserRole(user)"
              />
            </div>
          </ListItemAccount>
        </ul>
        <HeraDummyList
          v-if="!filteredUsers.length && isLoading"
          icon-name="dummy/members"
          size="200"
          position="center"
        >
          {{
            $t('_web_create-clan-team_fetching_users', 'Fetching list of users')
          }}
        </HeraDummyList>
        <HeraDummyList
          v-if="!filteredUsers.length && !isLoading && !userFilter"
          icon-name="dummy/members"
          size="200"
          position="center"
        >
          {{
            $t(
              '_web_create-clan-team_no_users',
              'There are no users in this list'
            )
          }}
        </HeraDummyList>
        <HeraDummyList
          v-if="userFilter && !filteredUsers.length"
          icon-name="dummy/members"
          size="200"
          position="center"
        >
          {{ $t('_web_create-clan-team_no_users', "We couldn't find anybody") }}
        </HeraDummyList>
      </Panel>
    </div>

    <div class="col-6">
      <Panel class="h-100">
        <Subheader
          class="mb-6"
          :text="$t('_web_create-clan-team_current_team', 'Current team')"
        />
        <div v-if="listTeamMembers.length > 0">
          <ul class="list-group list-group-flush bg-transparent w-100">
            <ListItemAccount
              v-for="user in listTeamMembers"
              :key="user.login"
              v-model="listTeamMembers"
              class="py-3 px-3 font-weight-bold"
              tag="li"
              :size="24"
              v-bind="{
                type: user.type,
                avatar: user.avatar,
                name: $filters.displayName(user),
                accountId: user.login,
                role: user.team_role === 'member' ? '' : user.team_role,
                vanityId: user.vanityId || user.vanity_id,
              }"
            >
              <Icon
                slot="action-before"
                name="arrow-triangle"
                width="24"
                class="link-remove-user h-mr-4 cursor-pointer"
                @click.native="changeUserRole(user)"
              />
              <div
                slot="action"
                class="col-auto d-flex align-items-center button-wrap"
              >
                <Icon
                  v-if="user.team_role !== 'captain'"
                  name="captain"
                  width="24"
                  @click.native="changeCaptain(user)"
                />
              </div>
            </ListItemAccount>
          </ul>
        </div>
        <HeraDummyList
          v-if="!listTeamMembers.length && isLoading"
          icon-name="dummy/members"
          size="200"
          position="center"
        >
          {{
            $t('_web_create-clan-team_fetching_users', 'Fetching list of users')
          }}
        </HeraDummyList>
        <HeraDummyList
          v-if="!listTeamMembers.length && !isLoading"
          icon-name="dummy/members"
          size="200"
          position="center"
        >
          {{
            $t(
              '_web_create-clan-team_no_users_yet',
              'There are no users in this list yet'
            )
          }}
        </HeraDummyList>
      </Panel>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { cloneDeep } from '@/utils/lodashUtils';
import HeraSearchInput from '@/components/molecules/form/HeraSearchInput';
import HeraDummyList from '@/components/common/HeraDummyList';
import ListItemAccount from '@/components/common/ListItemAccount';
import Panel from '@/components/atoms/Panel';
import Subheader from '@/components/atoms/Subheader';
import Icon from '@/components/atoms/Icon';
export default {
  name: 'ClanTeamMembers',
  inject: ['$validator'],
  components: {
    HeraDummyList,
    HeraSearchInput,
    ListItemAccount,
    Panel,
    Subheader,
    Icon,
  },
  props: {
    teamID: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      usersList: [],
      userFilter: '',
      captainIndex: -1,
      isLoading: false,
      selectedMember: '',
    };
  },
  computed: {
    ...mapGetters(['gameByID']),
    ...mapState('current', ['account', 'accountId']),
    clanId() {
      return (this.clan && this.clan.id) || this.accountId;
    },
    clanName() {
      return (
        this.$lodash.get(this, 'account._name') ||
        this.$lodash.get(this, 'clan.name')
      );
    },
    membersWithoutTeam() {
      return this.usersList.filter(member => member.team_role === 'none');
    },
    hasFreeMembers() {
      return this.membersWithoutTeam.length;
    },
    filteredUsers() {
      return this.membersWithoutTeam.filter(user =>
        user.login.toLowerCase().includes(this.userFilter.toLowerCase())
      );
    },
    listTeamMembers() {
      let membersList = this.usersList.filter(
        user => user.team_role === 'member' || user.team_role === 'captain'
      );
      if (membersList.length) {
        let index = membersList.findIndex(el => el.team_role === 'captain');
        membersList[0] = membersList.splice(index, 1, membersList[0])[0];
      }
      return membersList;
    },
    hasUsers() {
      return this.usersList && this.usersList.length;
    },
    isSelectedCaptain() {
      return this.captainIndex >= 0;
    },
  },
  watch: {
    listTeamMembers(val) {
      this.$emit('changeTeam', val);
    },
  },
  created() {
    this.getPageData();
    if (this.gameID) {
      this.game = this.gameByID(this.gameID);
    }
  },
  methods: {
    ...mapActions(['fetchClanMembers', 'errorNotify', 'fetchClanTeamMembers']),
    onFilter() {
      this.usersList = [];
      this.getPageData();
    },
    findUserIndex(user, key, value) {
      return this.usersList.findIndex(el =>
        value ? el[key] === value : el[key] === user[key]
      );
    },
    changeUserRole(user) {
      const i = this.findUserIndex(user, 'login');
      let memberRole = 'none';
      if (user.team_role === 'none') {
        if (!this.listTeamMembers.length) {
          memberRole = 'captain';
          this.captainIndex = i;
        } else {
          memberRole = 'member';
        }
      } else {
        if (user.team_role === 'captain') {
          this.captainIndex = -1;
        }
      }
      this.$set(this.usersList[i], 'team_role', memberRole);
    },
    changeCaptain(selectedMember) {
      const i = this.findUserIndex(selectedMember, 'login');
      this.$set(this.usersList[i], 'team_role', 'captain');
      if (this.captainIndex >= 0) {
        this.$set(this.usersList[this.captainIndex], 'team_role', 'member');
      }
      this.captainIndex = i;
    },
    getPageData() {
      this.isLoading = true;
      this.fetchClanMembers({ accountId: this.clanId }).then(res => {
        const { items } = res;
        this.usersList = items.map(user => ({
          ...cloneDeep(user),
          team_role: 'none',
        }));

        if (this.teamID) {
          let data = {
            clanID: this.clanId,
            teamID: this.teamID,
          };
          this.fetchClanTeamMembers(data)
            .then(res => {
              const tempUsersList = cloneDeep(this.usersList);
              res.forEach(member => {
                const index = this.findUserIndex(member, 'login');
                if (index > -1) {
                  tempUsersList[index].team_role = member.team_role;
                }
              });
              this.usersList = tempUsersList;
            })
            .then(() => {
              this.captainIndex = this.findUserIndex(
                null,
                'team_role',
                'captain'
              );
              this.isLoading = false;
            });
        } else {
          this.isLoading = false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
.teams-wrap {
  .link-remove-user {
    .rtl & {
      transform: rotate(0);
    }
    transform: rotate(180deg);
  }
  .link-add-user {
    .rtl & {
      transform: rotate(180deg);
    }
    transform: rotate(0);
  }
  .list-item-title {
    .role {
      font-size: $ph-caption-bold-font-size;
      line-height: $ph-caption-bold-font-size;
      text-transform: uppercase;
    }
  }
}
</style>
